import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Link2, Trash2, Play, Loader2, Film, Plus } from "lucide-react";
import { AfVideoPlayer } from "@/components/AfVideoPlayer";

const CHUNK = 5 * 1024 * 1024;
const MAX = 100 * 1024 * 1024;
const MUSCLES = ["Peito", "Costas", "Ombros", "Bíceps", "Tríceps", "Pernas", "Glúteos", "Panturrilha", "Abdômen", "Cardio", "Corpo inteiro", "Alongamento"];

const uploadVideo = async (file, ex, onPct) => {
  const uploadId = crypto.randomUUID();
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    Object.entries({ upload_id: uploadId, index: i, total, exercise_id: ex.id, exercise_name: ex.name, filename: file.name })
      .forEach(([k, v]) => fd.append(k, String(v)));
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    await api.post("/exercise-videos/upload-chunk", fd);
    onPct(Math.round(((i + 1) / total) * 100));
  }
};

const NewExerciseDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState({ name: "", muscle: "", description: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const save = async () => {
    if (!f.name.trim() || !f.muscle) return toast.error("Informe nome e grupo muscular");
    if (file && file.size > MAX) return toast.error("O vídeo passa de 100 MB");
    setBusy(true); setPct(0);
    try {
      const { data: ex } = await api.post("/exercise-library", { ...f, name: f.name.trim() });
      if (file) await uploadVideo(file, ex, setPct);
      toast.success(file ? "Exercício criado! Vídeo sendo comprimido…" : "Exercício criado");
      setF({ name: "", muscle: "", description: "" }); setFile(null);
      onCreated(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar exercício"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent data-testid="afv-new-dialog">
        <DialogHeader><DialogTitle>Novo exercício American Fit</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Supino inclinado com halteres" data-testid="afv-new-name"/></div>
          <div><Label>Grupo muscular *</Label>
            <select value={f.muscle} onChange={(e) => setF({ ...f, muscle: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-transparent px-3 text-sm" data-testid="afv-new-muscle">
              <option value="">Selecione…</option>
              {MUSCLES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div><Label>Descrição / instruções</Label><Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Como executar, postura, respiração…" data-testid="afv-new-description"/></div>
          <div><Label>Vídeo (opcional, até 100 MB)</Label><Input type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="afv-new-file"/></div>
          {busy && file && <div className="space-y-1"><Progress value={pct}/><div className="text-xs text-slate-500">Enviando {pct}%</div></div>}
          <Button onClick={save} disabled={busy} className="w-full" data-testid="afv-new-save">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Criar exercício"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VideoRow = ({ ex, video, onChanged, onPlay }) => {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [link, setLink] = useState("");
  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX) return toast.error("O vídeo passa de 100 MB");
    setBusy(true); setPct(0);
    try {
      await uploadVideo(file, ex, setPct);
      toast.success("Vídeo enviado! Comprimindo para MP4 720p… fica pronto em instantes.");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro no envio do vídeo"); }
    finally { setBusy(false); }
  };
  const saveLink = async () => {
    if (!/^https?:\/\//.test(link.trim())) return toast.error("Cole um link começando com http");
    try { await api.post("/exercise-videos/link", { exercise_id: ex.id, exercise_name: ex.name, url: link.trim() }); setLink(""); toast.success("Link salvo"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar link"); }
  };
  const remove = async () => {
    if (!window.confirm(`Excluir o vídeo American Fit de "${ex.name}"?`)) return;
    await api.delete(`/exercise-videos/${video.id}`); toast.success("Vídeo excluído"); onChanged();
  };
  const removeExercise = async () => {
    if (!window.confirm(`Excluir o exercício "${ex.name}" da biblioteca?`)) return;
    if (video) await api.delete(`/exercise-videos/${video.id}`);
    await api.delete(`/exercise-library/${ex.id}`); toast.success("Exercício excluído"); onChanged();
  };
  return (
    <Card className="p-4 border border-slate-200 space-y-2" data-testid={`afv-row-${ex.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-semibold text-slate-900">{ex.name}</div>
          <div className="text-xs text-slate-500">{ex.muscle}{ex.custom && <span className="ml-1 px-1.5 rounded bg-slate-100 text-slate-600 font-semibold">American Fit</span>}</div>
          {ex.description && <div className="text-xs text-slate-600 mt-1 whitespace-pre-line" data-testid={`afv-desc-${ex.id}`}>{ex.description}</div>}
        </div>
        {video ? (
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${video.status === "ready" ? "bg-emerald-100 text-emerald-700" : video.status === "error" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`} data-testid={`afv-status-${ex.id}`}>
              {video.status === "ready" ? (video.source === "link" ? "Link" : "Vídeo pronto") : video.status === "error" ? "Erro" : "Processando…"}
            </span>
            {video.status === "ready" && <Button size="sm" variant="outline" onClick={() => onPlay(video, ex.name)} data-testid={`afv-play-${ex.id}`}><Play className="w-3 h-3 mr-1"/>Ver</Button>}
            <Button size="sm" variant="ghost" onClick={remove} data-testid={`afv-delete-${ex.id}`}><Trash2 className="w-4 h-4 text-rose-600"/></Button>
          </div>
        ) : <span className="text-xs text-slate-400">Sem vídeo próprio</span>}
        {ex.custom && <Button size="sm" variant="ghost" onClick={removeExercise} className="text-rose-600" data-testid={`afv-delete-ex-${ex.id}`}>Excluir exercício</Button>}
      </div>
      {busy ? <div className="space-y-1"><Progress value={pct}/><div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin"/>Enviando {pct}%</div></div> : (
        <div className="flex flex-col sm:flex-row gap-2">
          <label className="inline-flex items-center justify-center gap-1 text-sm px-3 h-9 rounded-md border border-slate-300 cursor-pointer hover:bg-slate-50" data-testid={`afv-upload-${ex.id}`}>
            <Upload className="w-4 h-4"/>{video ? "Substituir por arquivo" : "Enviar arquivo"}
            <input type="file" accept="video/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`afv-file-${ex.id}`}/>
          </label>
          <div className="flex gap-2 flex-1">
            <Input placeholder="ou cole um link (YouTube, Vimeo, .mp4)" value={link} onChange={(e) => setLink(e.target.value)} data-testid={`afv-link-input-${ex.id}`}/>
            <Button size="sm" variant="outline" onClick={saveLink} className="h-9" data-testid={`afv-link-save-${ex.id}`}><Link2 className="w-4 h-4"/></Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default function ExerciseVideos() {
  const [lib, setLib] = useState([]);
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState("");
  const [play, setPlay] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = async () => {
    const [l, v] = await Promise.all([api.get("/exercise-library"), api.get("/exercise-videos")]);
    setLib(l.data); setVideos(v.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!videos.some(v => v.status === "processing")) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [videos]);
  const shown = useMemo(() => lib.filter(e => !q || `${e.name} ${e.muscle}`.toLowerCase().includes(q.toLowerCase())), [lib, q]);
  return (
    <div className="space-y-5" data-testid="exercise-videos-page">
      <PageHeader title="Vídeos American Fit" subtitle={`${videos.filter(v => v.status === "ready").length} vídeos próprios · aparecem no Portal do Aluno de todas as unidades`} />
      <Label className="flex items-center gap-2 text-slate-500 text-xs"><Film className="w-4 h-4"/>Arquivos até 100 MB são comprimidos automaticamente para MP4 até 720p.</Label>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input placeholder="Buscar exercício" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="afv-search"/>
        <Button onClick={() => setCreating(true)} data-testid="afv-new-btn"><Plus className="w-4 h-4 mr-1"/>Novo exercício</Button>
      </div>
      <NewExerciseDialog open={creating} onOpenChange={setCreating} onCreated={load}/>
      <div className="grid lg:grid-cols-2 gap-3">
        {shown.map(ex => <VideoRow key={ex.id} ex={ex} video={videos.find(v => v.exercise_id === ex.id)} onChanged={load} onPlay={(v, n) => setPlay({ video: v, name: n })}/>)}
      </div>
      <AfVideoPlayer video={play?.video} name={play?.name} onClose={() => setPlay(null)} />
    </div>
  );
}
